import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';

interface AuditEvent {
  id: string;
  kind: string;
  target_kind: string | null;
  target_id: string | null;
  actor_user_id: string | null;
  occurred_at: string;
  hash_self: string;
  hash_prev: string | null;
}

export function AuditLogPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const events = useQuery<AuditEvent[]>({
    queryKey: ['audit', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/audit`)).data,
  });
  const verify = useQuery<{ ok: boolean; brokenAt: string | null }>({
    queryKey: ['audit-verify', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/audit/verify`)).data,
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="display text-2xl font-semibold">{t('audit.title')}</h1>
            <p className="text-sm text-ink-500 mt-1">{t('audit.subtitle')}</p>
          </div>
          {verify.data && (
            <Pill tone={verify.data.ok ? 'success' : 'warn'}>
              <ShieldCheck size={12} /> {verify.data.ok ? t('audit.chainVerified') : t('audit.chainBroken')}
            </Pill>
          )}
        </div>

        <Card>
          {events.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !events.data || events.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('audit.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {events.data.map((e) => (
                <li key={e.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{e.kind}</div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {e.target_kind && <span>{e.target_kind}/{e.target_id?.slice(0, 8)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-ink-400">{e.hash_self.slice(0, 16)}…</span>
                    <span className="text-xs text-ink-500 num">{formatDate(e.occurred_at, i18n.language)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
